/**
 * useFirestoreDocument - Generic Firestore Document Hook
 *
 * Provides real-time listener for a single Firestore document with:
 * - Automatic subscription management
 * - Document parsing with type safety
 * - Loading/error state management
 * - Cache vs server read tracking
 *
 * Use this for single document listeners (e.g., user permissions, config docs).
 * For collection listeners, use useFirestoreCollection instead.
 */

import { useEffect } from 'react';
import {
  doc,
  onSnapshot,
  DocumentData,
  DocumentSnapshot,
  Firestore,
} from 'firebase/firestore';
import { logger } from '../../utils/logger';

export interface UseFirestoreDocumentOptions<T> {
  /** Firestore instance */
  db: Firestore | null;

  /** Document path (e.g., "users/john@example.com" or "config/settings") */
  documentPath: string | null;

  /** Parse Firestore document into typed object. Return null if document doesn't exist. */
  parseDocument: (data: DocumentData) => T | null;

  /** Callback when data is loaded (null if document doesn't exist) */
  onDataLoaded?: (data: T | null) => void;

  /** Callback when loading state changes */
  onLoadingChange?: (loading: boolean) => void;

  /** Callback when error occurs */
  onError?: (error: string) => void;

  /** Include metadata changes (for cache tracking) */
  includeMetadataChanges?: boolean;

  /** Enable/disable the listener (useful for conditional loading) */
  enabled?: boolean;
}

/**
 * Generic hook for listening to a single Firestore document
 *
 * @example
 * ```typescript
 * useFirestoreDocument({
 *   db,
 *   documentPath: `users/${userEmail}`,
 *   parseDocument: (data) => ({
 *     role: data.role || 'viewer',
 *     pages: data.pages || {},
 *     locations: data.locations || [],
 *   }),
 *   onDataLoaded: setUserPermissions,
 *   onLoadingChange: setLoading,
 *   onError: setError,
 * });
 * ```
 */
export function useFirestoreDocument<T>({
  db,
  documentPath,
  parseDocument,
  onDataLoaded,
  onLoadingChange,
  onError,
  includeMetadataChanges = true,
  enabled = true,
}: UseFirestoreDocumentOptions<T>) {
  useEffect(() => {
    // Guard: explicitly disabled - don't call onDataLoaded to avoid false "not found" warnings
    // The consumer will get data when enabled becomes true
    if (!enabled) {
      onLoadingChange?.(false);
      return undefined;
    }

    // Guard: missing dependencies - also don't trigger onDataLoaded
    // This prevents false warnings before Firebase auth completes
    if (!db || !documentPath) {
      onLoadingChange?.(false);
      return undefined;
    }

    onLoadingChange?.(true);

    try {
      const docRef = doc(db, documentPath);

      logger.firestore('Subscribe', `doc:${documentPath}`);

      // Set up real-time listener
      const unsubscribe = onSnapshot(
        docRef,
        { includeMetadataChanges },
        (snapshot: DocumentSnapshot<DocumentData>) => {
          try {
            const source = snapshot.metadata.fromCache ? 'cache' : 'server';
            const exists = snapshot.exists();
            logger.firestore('Snapshot', `doc:${documentPath} (exists: ${exists}, from ${source})`);

            if (!exists) {
              onDataLoaded?.(null);
              onLoadingChange?.(false);
              return;
            }

            const data = snapshot.data();
            if (!data) {
              onDataLoaded?.(null);
              onLoadingChange?.(false);
              return;
            }

            const parsed = parseDocument(data);
            onDataLoaded?.(parsed);
            onLoadingChange?.(false);
          } catch (error) {
            logger.error('[useFirestoreDocument] Error processing snapshot:', error);
            onError?.('Failed to process data');
            onLoadingChange?.(false);
          }
        },
        (error) => {
          logger.error('[useFirestoreDocument] Listener error:', error);
          onError?.(error.message || 'Failed to load data');
          onLoadingChange?.(false);
        }
      );

      return () => {
        logger.firestore('Unsubscribe', `doc:${documentPath}`);
        unsubscribe();
      };
    } catch (error) {
      logger.error('[useFirestoreDocument] Setup error:', error);
      onError?.('Failed to initialize listener');
      onLoadingChange?.(false);
      return undefined;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    db,
    documentPath,
    enabled,
    includeMetadataChanges,
    // Note: Callbacks (parseDocument, onDataLoaded, onLoadingChange, onError) are
    // intentionally omitted to prevent effect re-runs when callback references change.
    // They should be stable references from stores or wrapped in useCallback by the caller.
  ]);
}
